import { Box, Divider, useMediaQuery } from '@mui/material';
import * as loadingAnimationData from 'core/assets/lottie/green-loader-animation.json';
import BaseTypography from 'core/components/typography/BaseTypography';
import { AccountsPagePlans } from 'core/constants/enums/AccountsPagePlans.ts';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { CurrentPlan } from 'core/constants/enums/CurrentPlan.ts';
import { FontsEnum } from 'core/constants/enums/FontsEnum.ts';
import { SubscriptionPlanType } from 'core/constants/enums/SubscriptionPlanType.ts';
import { gtagAnalyticsEvents } from 'core/constants/gtagAnalyticsEvents';
import { metaAnalyticsEvents } from 'core/constants/metaAnalyticsEvents';
import { redditAnalyticsEvents } from 'core/constants/redditAnalyticsEvents';
import { twitterAnalyticsEvents } from 'core/constants/twitterAnalyticsEvents';
import LottieAnimation from 'core/layouts/components/lottie-animation/LottieAnimation';
import OneTimeSubscriptionCard from 'core/layouts/components/subscription-plans/components/one-time-pack-card/OneTimeSubscriptionCard';
import PayLinkPopUp from 'core/layouts/components/subscription-plans/components/pay-link-pop-ups/PayLinkPopUp';
import PlanTypeToggleDropDown from 'core/layouts/components/subscription-plans/components/plan-type-toggle-dropdown/PlanTypeToggleDropDown';
import ProratedPricePopUp from 'core/layouts/components/subscription-plans/components/prorated-price-pop-up/ProratedPricePopUp';
import SubscriptionCard from 'core/layouts/components/subscription-plans/components/subscription-card/SubscriptionCard';
import { TermsAndConditions } from 'core/layouts/components/subscription-plans/components/terms-and-conditions/TermsAndConditions';
import { subscriptionPlansStyles } from 'core/layouts/components/subscription-plans/SubscriptionPlansStyles';
import appTheme from 'core/layouts/ui-blocks/AppTheme';
import { ConditionalLayer } from 'core/layouts/ui-blocks/ReusableComponents';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { ACCOUNT, PAYMENT_ERROR, PAYMENT_SUCCESSFUL } from 'core/navigation/Routes';
import {
	getUserProfileAPIStageSelector,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import {
	getManageSubscriptionsLinkAPIStageSelector,
	getOneTimePayLinkAsync,
	getSubscriptionPayLinkAsync,
	getSubscriptionPlans,
	getSubscriptionPlansApiStageSelector,
	getSubscriptionProratedPriceAsync,
	subscriptionPlansSelector,
} from 'core/store/reducers/profileReducer/ProfileSlice';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { gtagAnalyticsUtil } from 'core/utils/gtagAnalyticsUtil';
import { metaAnalyticsUtil } from 'core/utils/metaAnalyticsUtil';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import { redditAnalyticsUtil } from 'core/utils/redditAnalyticsUtil';
import { twitterAnalyticsUtil } from 'core/utils/twitterAnalyticsUtil';
import { getArrayTillN } from 'core/utils/utils';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

function SubscriptionPlans({ posthogEvent = null, amplitudeEvent = null }) {
	const location = useLocation();
	const cancelUrl = `${window.location.origin}${location.pathname}?${location.search}`;
	const successUrl = `${window.location.origin}/${ACCOUNT}/${PAYMENT_SUCCESSFUL}`;
	const dispatch = useDispatch();
	const tabs = [
		{
			label: 'Monthly',
			value: SubscriptionPlanType.MONTHLY,
		},
		{
			label: 'Yearly (40% off)',
			value: SubscriptionPlanType.ANNUALLY,
			chip: (
				<BaseTypography
					id="plan-type-tab-badge"
					key="plan-type-tab-badge"
					variant="b2_caption_medium"
					styles={subscriptionPlansStyles.planTypeTabBadge}
				>
					40% 0ff
				</BaseTypography>
			),
		},
	];

	const subscriptionPlans = useSelector(subscriptionPlansSelector);
	const subscriptionPlansApiStage = useSelector(getSubscriptionPlansApiStageSelector);
	// const subscriptionCouponCodeDetails = useSelector(subscriptionCouponDetailsSelector);
	const getManageSubscriptionsLinkAPIStage = useSelector(
		getManageSubscriptionsLinkAPIStageSelector,
	);
	const profile = useSelector(userProfileSelector);
	const getProfileAPIStage = useSelector(getUserProfileAPIStageSelector);

	const isFreeUser = profile?.currentPlan === AccountsPagePlans.FREE;

	const defaultTab = tabs[1];
	const [selectedPlanType, setSelectedPlanType] = useState(defaultTab);
	const isMobileScreen = useMediaQuery(appTheme.breakpoints.down('md'));
	useEffect(() => {
		dispatch(getSubscriptionPlans());
	}, [dispatch]);

	const handleSubscriptionPlanButtonClick = (plan, planType) => {
		capturePosthogEvent(posthogEvent, { pack_name: plan.name });
		captureAmplitudeEvent(amplitudeEvent, { pack_name: plan.name });
		gtagAnalyticsUtil(gtagAnalyticsEvents.BEGIN_CHECKOUT);
		twitterAnalyticsUtil(twitterAnalyticsEvents.BEGIN_CHECKOUT);
		redditAnalyticsUtil(redditAnalyticsEvents.BEGIN_CHECKOUT);
		metaAnalyticsUtil(metaAnalyticsEvents.BEGIN_CHECKOUT);
		if (planType === SubscriptionPlanType.ONE_TIME) {
			dispatch(
				getOneTimePayLinkAsync({
					value: plan.price,
					successUrl: `${window.location.origin}/${ACCOUNT}/${PAYMENT_SUCCESSFUL}${!Number.isNaN(plan.price / 100) ? `?value=${plan.price / 100}` : ''}`,
					cancelUrl: `${window.location.origin}/${ACCOUNT}/${PAYMENT_ERROR}`,
				}),
			);
		} else if (profile.showProratedPrices) {
			dispatch(
				getSubscriptionProratedPriceAsync({
					price: plan.price,
					subscriptionType: selectedPlanType.value,
					successUrl: `${successUrl}${!Number.isNaN(plan.price / 100) ? `?value=${plan.price / 100}` : ''}`,
					cancelUrl,
				}),
			);
		} else {
			dispatch(
				getSubscriptionPayLinkAsync({
					price: plan.price,
					subscriptionType: selectedPlanType.value,
					successUrl: `${successUrl}${!Number.isNaN(plan.price / 100) ? `?value=${plan.price / 100}` : ''}`,
					cancelUrl,
				}),
			);
		}
	};

	const disableSubscribeButton = (plan) =>
		(plan?.name?.toLowerCase() === CurrentPlan.FREE &&
			profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE) ||
		(profile?.currentPlan?.toLowerCase() === plan.name.toLowerCase() &&
			selectedPlanType.value === profile?.validity);

	return (
		<Box
			id="subscription-comp"
			sx={{
				...reusableStyles.flexColWithGap(120),
				width: '100%',
				gap: { xs: '80px', sm: '120px' },
			}}
		>
			<PayLinkPopUp />
			<ProratedPricePopUp />

			<Box id="subscription-comp-top-half" sx={subscriptionPlansStyles.subscriptionComp}>
				<PlanTypeToggleDropDown
					tabs={tabs}
					selectedPlanType={selectedPlanType}
					setSelectedPlanType={setSelectedPlanType}
				/>
				<style>
					{`.os-scrollbar-handle {
                  background-color: #2C2C2C !important;
              }
            `}
				</style>
				<OverlayScrollbarsComponent style={{ width: '100%' }}>
					<Box
						id="subscription-plans-section"
						sx={[subscriptionPlansStyles.subscriptionPlansSection]}
					>
						{(subscriptionPlansApiStage === ApiStage.LOADING ||
							getProfileAPIStage === ApiStage.LOADING) &&
							getArrayTillN(5).map((skLoader) => (
								<SubscriptionCard
									key={`sk-loader-${skLoader}`}
									id={`sk-loader-${skLoader}`}
									loading
								/>
							))}
						{subscriptionPlansApiStage === ApiStage.SUCCESS &&
							getProfileAPIStage === ApiStage.SUCCESS &&
							subscriptionPlans[SubscriptionPlanType.ONE_TIME]?.length > 0 &&
							!isFreeUser && (
								<>
									<Box
										sx={[
											reusableStyles.flexColWithGap(16),
											{ alignItems: { xs: 'center', sm: 'flex-start' } },
										]}
									>
										<OneTimeSubscriptionCard
											handleSubscriptionPlanButtonClick={handleSubscriptionPlanButtonClick}
											subscriptionPlans={subscriptionPlans}
											isFreeUser={isFreeUser}
										/>
									</Box>
									<ConditionalLayer isShown={!isMobileScreen}>
										<Divider
											style={{ margin: '40px 0 16px 0' }}
											orientation="vertical"
											flexItem
											sx={{ backgroundColor: 'colors.dark1' }}
										/>
									</ConditionalLayer>
								</>
							)}

						<Box
							sx={[
								reusableStyles.flexColWithGap(16),
								{ width: '100%', alignItems: { xs: 'center', sm: 'flex-start' } },
							]}
						>
							<BaseTypography
								id="upgrade-subscription-text"
								key="upgrade-subscription-text"
								variant="t1_bold"
								styles={{
									color: 'colors.light1',
									textAlign: 'left',
									fontFamily: FontsEnum.ZODIAK,
								}}
							>
								Upgrade Subscription
							</BaseTypography>
							<Box
								id="subscription-card-wrapper"
								sx={[
									reusableStyles.flexRowWithGap(16),
									{
										flexDirection: { xs: 'column', sm: 'row' },
										alignItems: { xs: 'center', sm: 'flex-start' },
									},
								]}
							>
								{subscriptionPlansApiStage === ApiStage.SUCCESS &&
									getProfileAPIStage === ApiStage.SUCCESS &&
									[...subscriptionPlans[selectedPlanType.value]]?.map((plan) => (
										<Box id="subscription-card" key={`subscription-${plan.name}`}>
											<SubscriptionCard
												key={`subscription-${plan.name}`}
												id={`subscription-${plan.name}`}
												data-testid="subscription-card"
												loading={false}
												plan={plan}
												planType={selectedPlanType.value}
												buttonText={
													selectedPlanType.value === SubscriptionPlanType.ONE_TIME
														? 'Buy now'
														: disableSubscribeButton(plan)
															? 'Current plan'
															: profile.showProratedPrices
																? 'Upgrade'
																: 'Subscribe'
												}
												onButtonClick={() => handleSubscriptionPlanButtonClick(plan)}
												disableButton={disableSubscribeButton(plan)}
											/>
										</Box>
									))}
							</Box>
						</Box>
					</Box>
				</OverlayScrollbarsComponent>
				<Box
					sx={subscriptionPlansStyles.processManageSubscription(
						getManageSubscriptionsLinkAPIStage === ApiStage.LOADING,
					)}
				>
					<LottieAnimation animationData={loadingAnimationData} loop />
				</Box>
			</Box>
			{subscriptionPlansApiStage === ApiStage.SUCCESS &&
				getProfileAPIStage === ApiStage.SUCCESS &&
				subscriptionPlans[SubscriptionPlanType.ONE_TIME]?.length > 0 &&
				isFreeUser && (
					<OneTimeSubscriptionCard
						handleSubscriptionPlanButtonClick={handleSubscriptionPlanButtonClick}
						subscriptionPlans={subscriptionPlans}
						isFreeUser={isFreeUser}
					/>
				)}
			<TermsAndConditions />
		</Box>
	);
}

export default SubscriptionPlans;
